import { useFeatureSwitch, useWorkspaceId } from "@skilitics/designer-auth/state";
import { useModalDialog } from "@skilitics/designer-ui-components";
import { FormattedMessage, type FormatMessages } from "@skilitics/localization";
import type { NodeTemplate, NodeTemplateCategory } from "@skilitics/nautilus-endpoint-definition";

import { useContentHostId } from "../../../backend";
import { useRemoveNodeTemplate, useUpdateNodeTemplate } from "../api";
import { canWriteNodeTemplate, NodeTemplateForm, type FormValues, useCanWriteNodeTemplate } from "../shared";
import { uploadedThumbnailAsset } from "../shared/helpers.tsx";
import { useGetThumbnailAssetId } from "../shared/useGetThumbnailAssetId.ts";

interface Props {
  template: NodeTemplate;
  onSaved: () => void;
  onCancel: () => void;
  onRemoved: () => void;
  categories: NodeTemplateCategory[];
}

const dialogId = "confirm-template-deletion";

export const EditTemplate = ({ template, onSaved, onCancel, onRemoved, categories }: Props) => {
  const { openModalDialog, closeModalDialog } = useModalDialog();

  const isSuperUser = useFeatureSwitch("super-user");
  const canWrite = useCanWriteNodeTemplate(template);

  const { updateAsync, status: updateStatus } = useUpdateNodeTemplate();
  const { removeAsync, status: removeStatus } = useRemoveNodeTemplate();

  const hostId = useContentHostId();

  const getThumbnailAssetId = useGetThumbnailAssetId();

  const initialValues: FormValues = {
    title: template.title,
    description: template.description ?? "",
    tags: template.tags,
    scope: template.scope.type,
    category: template.category,
    thumbnail: template.thumbnailAssetId ? uploadedThumbnailAsset(template.thumbnailAssetId) : undefined,
  };

  const handleSubmit = async (values: FormValues) => {
    // Currently will never be a local thumbnail since we don't pass the createDefaultThumbnail,
    // but it's easier to just use the same logic anyway.
    const thumbnailAssetId = await getThumbnailAssetId(values.thumbnail);

    await updateAsync({
      id: template.id,
      title: values.title,
      description: values.description || undefined,
      tags: values.tags,
      scope: values.scope === "module" ? { type: "module", moduleId: hostId } : { type: values.scope },
      category: values.category,
      thumbnailAssetId,
    });

    onSaved();
  };

  const handleRemove = () => {
    const handleConfirm = async (confirmed: boolean) => {
      closeModalDialog(dialogId);

      if (confirmed) {
        await removeAsync(template.id);

        onRemoved();
      }
    };

    openModalDialog(
      {
        header: <FormattedMessage messages={formatStrings.header} />,
        children: <FormattedMessage messages={formatStrings.content} values={{ title: template.title }} />,
        buttons: "confirm",
        cancelLabel: <FormattedMessage messages={formatStrings.cancelLabel} />,
        confirmLabel: <FormattedMessage messages={formatStrings.confirmLabel} />,
        onConfirm: handleConfirm,
      },
      dialogId,
    );
  };

  return (
    <NodeTemplateForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      onRemove={canWrite ? handleRemove : undefined}
      canSetSystemScope={isSuperUser}
      canAddCategories={isSuperUser}
      deleting={removeStatus === "pending"}
      saving={updateStatus === "pending"}
      categories={categories}
    />
  );
};

const formatStrings = {
  header: {
    en: "Delete Template",
  },
  content: {
    en: `Are you sure you want to delete template "{title}"? This cannot be undone.`,
  },
  cancelLabel: {
    en: "Cancel",
  },
  confirmLabel: {
    en: "Yes, Delete",
  },
} satisfies FormatMessages;
